import { SDK } from '@commercetools/frontend-sdk';
import { getLocalizationInfo } from '@/project.config';
import { ComposableCommerceB2B, ComposableCommerceEventsB2B } from './composable-commerce-b2b';
import { CustomEvents } from './CustomEvents';
import getCookieValue from '@/utils/cookie';
import { generateQueryString } from '@/utils/string-herlpers';
import { decodeToken } from '@/utils/token';
import axios, { AxiosRequestConfig } from 'axios';

function setSessionCookie(name: string, value: string): void {
  const maxAge = 25 * 24 * 60 * 60;
  document.cookie = `${name}=${value}; path=/; SameSite=Lax; max-age=${maxAge}`;
}

export class ResponseError extends Error {
  private readonly response: Response;

  constructor(response: Response) {
    super(`Got HTTP status code ${response.status} (${response.statusText})`);
    this.response = response;
  }

  getResponse() {
    return this.response;
  }

  getStatus() {
    return this.response.status;
  }
}
// Add other integration's custom events to the SDK's generic type here,
// by extending ComposableCommerceEvents with their type using an intersection.
// For example, <ComposableCommerceEvents & OtherEvents>.
// You may also wish to add your own custom events.
class CommercetoolsSDK extends SDK<ComposableCommerceEventsB2B & CustomEvents> {
  composableCommerce!: ComposableCommerceB2B;
  config: Record<string, string>;
  tokenPromise: Promise<string> | null;
  // Add any other integrations here.

  constructor() {
    super();
    this.config = {};
    this.tokenPromise = null;
    this.composableCommerce = new ComposableCommerceB2B(this as any);
    // Initialize your other integrations here.

    this.on('errorCaught', (event) => {
      // Globally handle any errors caught by the SDK and integrations. For
      // example, log error, fire notification, etc.
      console.log('SDK error: ', event.data);
      if (event.data?.error?.message?.includes('UNAUTHORIZED_ACCESS') && (event.data as any).tokenRefreshed) {
        // Logout and redirect to login
        const event = new CustomEvent('LOGOUT_USER');
        window.dispatchEvent(event);
      }
    });

    // Removed for performance - these console.logs with large event.data objects
    // were causing significant slowdown during page navigation
    // this.on('fetchCalled', (event) => {
    //   console.log(`${event.data.type === 'action' ? event.data.actionName : event.data.method} called: `, event.data);
    // });

    // this.on('fetchSuccessful', (event) => {
    //   console.log(
    //     `${event.data.type === 'action' ? event.data.actionName : event.data.method}  successful: `,
    //     event.data,
    //   );
    // });

    // Set up any other custom global event handlers here.
    // Ensure types are created and added to the SDK generic type
    // if specific to your project.
  }

  // A simplified, reusable method for configuring the SDK, as for
  // most cases only the locale and currency require input from runtime, or
  // may change on user input.
  defaultConfigure(localeString: string) {
    const { locale, currency } = getLocalizationInfo(localeString);
    const config = {
      locale: locale.replace('-', '_'),
      currency,
      extensionVersion: process.env.NEXT_PUBLIC_EXT_BUILD_ID ?? 'dev',
      endpoint: process.env.NEXT_PUBLIC_FRONTASTIC_HOST as string,
      sessionLifetime: 25 * 24 * 60 * 60 * 1000, // 25 days in ms
    };
    this.config = config;
    sdk.configure(config);
  }

  getDefaultHeaders() {
    return {
      'commercetools-frontend-extension-version': process.env.NEXT_PUBLIC_EXT_BUILD_ID ?? 'dev',
      'content-type': 'application/json',
      'frontastic-currency': this.config.currency,
      'frontastic-locale': this.config.locale,
      'sec-fetch-dest': 'empty',
      'sec-fetch-mode': 'cors',
      'sec-fetch-site': 'cross-site',
    };
  }

  // Add the missing methods required by CommercetoolsSdk interface
  async call<T>({
    actionName,
    payload,
    method,
    query,
  }: {
    actionName: string;
    payload?: Record<string, any>;
    method?: string;
    query?: any;
  }): Promise<{ data?: T; isError: boolean; statusCode?: number; error?: string | object }> {
    const params = query ? generateQueryString(query) : '';
    const endpoint = `${process.env.NEXT_PUBLIC_FRONTASTIC_HOST as string}/action/${actionName}${params}`;
    const config = {
      headers: {
        ...this.getDefaultHeaders(),
        'frontastic-session': getCookieValue('frontastic-session'),
        'x-frontastic-access-token': 'APIKEY',
      },
      body: payload ? (typeof payload === 'object' ? JSON.stringify(payload) : payload) : undefined,
      method: method ?? 'POST',
    };
    return await fetch(endpoint, config)
      .then(async (response) => {
        if (response.ok) {
          const frontasticSessionCookie: string | null = response.headers.get('frontastic-session');
          if (frontasticSessionCookie) {
            setSessionCookie('frontastic-session', frontasticSessionCookie);
          }
          const data = await response.json();
          return {
            data,
            isError: false,
          };
        }
        throw new ResponseError(response);
      })
      .catch(async (err: ResponseError) => {
        if (err && err.getResponse) {
          const response = err.getResponse();
          let error: Record<string, any>;
          try {
            error = await response.json();
          } catch {
            error = {
              message: await response.text(),
              error: true,
            };
          }
          if (typeof error.error === 'object') {
            error = error.error;
          }
          let tokenRefreshed = false;
          if (error?.message?.includes('UNAUTHORIZED_ACCESS')|| error?.errorMessage === 'InvalidToken' ||   error?.data?.errorMessage === 'InvalidToken' ) {
            const sessionToken = getCookieValue('frontastic-session');
            if (sessionToken) {
              const decodedToken = decodeToken(sessionToken);
              const { authRefreshToken } = decodedToken || {};
              if (authRefreshToken) {
                // Prevent concurrent token refresh calls
                if (!this.tokenPromise) {
                  this.tokenPromise = this.fetchToken();
                }
                const newToken = await this.tokenPromise;
                this.tokenPromise = null;

                if (newToken) {
                  tokenRefreshed = true;
                  try {
                    config.headers['frontastic-session'] = getCookieValue('frontastic-session');
                    const retryResponse = await fetch(endpoint, config);
                    if (retryResponse.ok) {
                      const data = await retryResponse.json();
                      return {
                        data,
                        isError: false,
                      };
                    }
                  } catch (e: any) {
                    console.log('----- call failed again with new token:', e);
                  }
                } else {
                  console.log('##### Token refresh returned empty token');
                }
              }
            }
          }
          const event = {
            eventName: 'errorCaught',
            data: {
              error,
              tokenRefreshed,
            },
          } as any;
          this.trigger(event);
          return { statusCode: response?.status || 400, error, isError: true };
        } else {
          return { error: err, isError: true };
        }
      });
  }

  async fetchToken(): Promise<string> {
    try {
      const config: AxiosRequestConfig = {
        method: 'POST',
        url: `${process.env.NEXT_PUBLIC_FRONTASTIC_HOST as string}/action/account/fetchToken`,
        headers: {
          ...this.getDefaultHeaders(),
          'frontastic-session': getCookieValue('frontastic-session'),
          'x-frontastic-access-token': 'APIKEY',
        },
      };
      const response = await axios(config);
      const token = response.data?.accessToken || '';
      const frontasticSessionCookie: string | undefined = response.headers['frontastic-session'];
      if (token && frontasticSessionCookie) {
        setSessionCookie('frontastic-session', frontasticSessionCookie);
      }
      return token;
    } catch {
      console.log('##### Refresh token failed');
      return '';
    }
  }

  async callActionWrapper() {
    if (typeof window === 'undefined') {
      // @ts-ignore
      return this.callAction.apply(this, arguments);
    } else {
      // @ts-ignore
      return this.call.apply(this, arguments);
    }
  }
}

// Create a single instance of the sdk.
const sdk = new CommercetoolsSDK();

// Export only the instance to serve as a singleton throughout
// the project.
export { sdk };